import { useState, useCallback, useEffect, useRef } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EntityTable } from "./EntityTable";
import { GenerateButton } from "./GenerateButton";
import { TabHelperBox } from "./TabHelperBox";
import { useToast } from "@/hooks/use-toast";
import { Info, Link2, Loader2 } from "lucide-react";
import { linkEntitiesToNodes as linkEntitiesToNodesService } from "@/services/entityLinkingService";
import type {
  ThematicMap,
  ThematicEntity,
  ThematicNodeWithEntity,
  NewThematicEntity,
  PromptStep,
  LLMEntityOutput,
} from "@/types/thematicMaps";

interface EntitiesSectionProps {
  map: ThematicMap;
  entities: ThematicEntity[];
  entitiesLoading: boolean;
  nodes: ThematicNodeWithEntity[];
  generating: boolean;
  currentStep: PromptStep | null;
  runEntityExtraction: (
    map: ThematicMap,
    nodes: ThematicNodeWithEntity[],
    onProgress?: (msg: string) => void,
  ) => Promise<LLMEntityOutput[] | null>;
  onCreateEntity: (
    data: Omit<NewThematicEntity, "user_id" | "map_id">,
  ) => Promise<ThematicEntity | null>;
  onCreateEntitiesBulk: (
    data: Omit<NewThematicEntity, "user_id" | "map_id">[],
  ) => Promise<ThematicEntity[]>;
  onDeleteEntity: (id: string) => Promise<boolean>;
  onRefreshEntities: () => Promise<void>;
  onRefreshNodes: () => Promise<void>;
  onUpdateNode: (
    id: string,
    updates: Record<string, unknown>,
  ) => Promise<boolean>;
}

const HELPER_ITEMS = [
  '<strong>Canonical Name</strong> — glowna nazwa encji (np. "ryz do sushi", "nori"). Unikalna w obrebie mapy.',
  "<strong>Typ</strong> — kategoria: concept, product, method, tool, role, problem, attribute.",
  '<strong>Aliasy</strong> — alternatywne nazwy (np. "ryz sushi" = "ryz do sushi").',
  "<strong>Wyciagnij z AI</strong> — automatycznie wyciaga encje z raw entities w nodes i linkuje je.",
  "<strong>Linkuj</strong> — recznie ponawia laczenie encji z nodes (dopasowuje po nazwie i aliasach).",
];

export function EntitiesSection({
  map,
  entities,
  entitiesLoading,
  nodes,
  generating,
  currentStep,
  runEntityExtraction,
  onCreateEntity,
  onCreateEntitiesBulk,
  onDeleteEntity,
  onRefreshNodes,
  onUpdateNode,
}: EntitiesSectionProps) {
  const { toast } = useToast();
  const [linking, setLinking] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Elapsed timer
  useEffect(() => {
    if (statusMessage) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      setElapsed(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [statusMessage]);

  const hasNodes = nodes.length > 0;
  const hasEntities = entities.length > 0;
  const nodesHaveRawEntities = nodes.some((n) => n.primary_entity_raw?.trim());
  const nodesLinked = nodes.filter((n) => n.primary_entity_id != null).length;
  const nodesUnlinked = nodes.length - nodesLinked;

  // Entity -> Node linking — deleguje do service (STOP-C).
  // Service robi UPDATE per node bezposrednio przez supabase (single source of truth
  // dla findEntity match strategy). UI tu wrapper z toast + state + refresh.
  const linkEntitiesToNodes = useCallback(
    async (entityList?: ThematicEntity[]) => {
      const entitiesToUse = entityList || entities;
      if (entitiesToUse.length === 0) return;

      setLinking(true);
      const outcome = await linkEntitiesToNodesService(
        map.id,
        entitiesToUse,
        nodes,
      );
      setLinking(false);

      if (outcome.linked > 0) {
        toast({
          title: "Linkowanie zakonczone",
          description:
            `Polaczono encje z ${outcome.linked} nodes` +
            (outcome.unmatched.length > 0
              ? ` (${outcome.unmatched.length} bez dopasowania)`
              : ""),
        });
        await onRefreshNodes();
      } else if (outcome.unmatched.length > 0) {
        const examples = outcome.unmatched
          .slice(0, 3)
          .map((r) => `"${r}"`)
          .join(", ");
        toast({
          title: "Brak dopasowan",
          description: `${outcome.unmatched.length} encji bez matcha: ${examples}${outcome.unmatched.length > 3 ? "..." : ""}`,
          variant: "destructive",
        });
      } else if (outcome.failed > 0) {
        toast({
          title: "Blad linkowania",
          description: outcome.firstError || "Niektore UPDATE padly",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Wszystko polaczone",
          description: "Wszystkie nodes maja juz przypisane encje",
        });
      }
    },
    [entities, nodes, map.id, onRefreshNodes, toast],
  );

  // Entity extraction
  const handleExtractEntities = useCallback(async () => {
    setStatusMessage("Przygotowywanie ekstrakcji encji...");
    try {
      const result = await runEntityExtraction(map, nodes, setStatusMessage);
      if (!result) return;

      setStatusMessage(
        `Wyekstrahowano ${result.length} encji. Sprawdzanie duplikatow...`,
      );

      const existingNames = new Set(
        entities.map((e) => e.canonical_name.toLowerCase()),
      );
      const newEntities = result.filter(
        (e) => !existingNames.has(e.canonical_name.toLowerCase()),
      );

      if (newEntities.length === 0) {
        toast({
          title: "Info",
          description: "Wszystkie encje juz istnieja. Uruchamiam linkowanie...",
        });
        setStatusMessage("Linkowanie encji z nodes...");
        await linkEntitiesToNodes();
        return;
      }

      setStatusMessage(
        `Zapisywanie ${newEntities.length} nowych encji do bazy...`,
      );
      const saved = await onCreateEntitiesBulk(
        newEntities.map((e) => ({
          canonical_name: e.canonical_name,
          entity_type: e.entity_type,
          aliases: e.aliases || [],
          description: e.description || "",
        })),
      );

      if (saved.length > 0) {
        setStatusMessage(
          `Zapisano ${saved.length} encji. Linkowanie z nodes...`,
        );
        const allEntities = [...entities, ...saved];
        await linkEntitiesToNodes(allEntities);
      }
    } finally {
      setStatusMessage(null);
    }
  }, [
    map,
    nodes,
    entities,
    runEntityExtraction,
    onCreateEntitiesBulk,
    linkEntitiesToNodes,
    toast,
  ]);

  return (
    <div className="space-y-4">
      <TabHelperBox items={HELPER_ITEMS} />

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Encje ({entities.length})</h3>
        <div className="flex items-center gap-2">
          {hasEntities && nodesUnlinked > 0 && (
            <GenerateButton
              onClick={() => linkEntitiesToNodes()}
              generating={linking}
              label={`Linkuj (${nodesUnlinked})`}
              loadingLabel="Linkowanie..."
              variant="secondary"
              size="sm"
            />
          )}
          <GenerateButton
            onClick={handleExtractEntities}
            generating={generating && currentStep === "entity_extraction"}
            label="Wyciagnij encje z AI"
            loadingLabel="Ekstrakcja encji..."
            disabled={!hasNodes || !nodesHaveRawEntities}
          />
        </div>
      </div>

      {/* Progress */}
      {statusMessage && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span className="flex-1">{statusMessage}</span>
          {elapsed > 0 && (
            <span className="text-xs text-blue-500 tabular-nums">
              {elapsed}s
            </span>
          )}
        </div>
      )}

      {/* Gating */}
      {!hasNodes && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Najpierw wygeneruj Nodes w zakladce "Klastry i tematy".
          </AlertDescription>
        </Alert>
      )}

      {/* Linking summary */}
      {hasNodes && hasEntities && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5" />
          <span>
            {nodesLinked}/{nodes.length} nodes polaczonych z encjami
            {nodesUnlinked > 0 && (
              <span className="text-amber-600 dark:text-amber-400 ml-1">
                ({nodesUnlinked} do polaczenia)
              </span>
            )}
          </span>
        </div>
      )}

      <EntityTable
        entities={entities}
        loading={entitiesLoading}
        onCreateEntity={onCreateEntity}
        onDeleteEntity={onDeleteEntity}
      />
    </div>
  );
}
